import {
  Plus,
  Users,
  Edit2,
  Trash2,
  KeyRound,
  UserMinus,
  Clock,
  Copy,
  XCircle,
  Search,
  ChevronLeft,
  ChevronRight,
  X,
  ExternalLink,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { LoadingSpinner } from "@/components/ui/loading";
import { Modal } from "@/components/ui/modal";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { formatRelativeTime, formatDate, formatTimeUntil } from "@/lib/utils";
import { roleLabel } from "@/lib/display-labels";
import { UserRole } from "@/types/api";
import { useActiveRole } from "@/features/auth/hooks/use-active-role";
import { UserModal } from "./user-modal";
import { ResetPasswordModal } from "./reset-password-modal";
import { AddUserModal } from "@/features/organizations/components/add-user-modal";
import type { UsersPageState } from "../hooks/use-users-page";

type BadgeVariant =
  | "default"
  | "success"
  | "warning"
  | "error"
  | "info"
  | "secondary";

const roleVariants: Record<UserRole, BadgeVariant> = {
  [UserRole.Owner]: "secondary",
  [UserRole.Admin]: "info",
  [UserRole.Manager]: "warning",
  [UserRole.Viewer]: "default",
};

const ROLE_FILTER_OPTIONS = [
  { value: "", label: "All Roles" },
  { value: "Owner", label: "Sys Admin" },
  { value: "Admin", label: "Admin" },
  { value: "Manager", label: "Manager" },
  { value: "Viewer", label: "Viewer" },
];

const STATUS_FILTER_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

export function UsersView({ state }: { state: UsersPageState }) {
  const navigate = useNavigate();
  const {
    users,
    pendingInvites,
    loading,
    totalCount,
    totalPages,
    page,
    currentUser,
  } = state;

  const activeRole = useActiveRole();
  const canWrite = activeRole === UserRole.Admin
    || activeRole === UserRole.Owner
    || activeRole === UserRole.Manager;

  const {
    roleFilter,
    statusFilter,
    searchInput,
    setSearchInput,
    handleClearFilters,
    handleRoleChange,
    handleStatusChange,
    handlePageChange,
    isUserModalOpen,
    setIsUserModalOpen,
    editingUser,
    handleEditUser,
    handleSaveUser,
    isResetModalOpen,
    setIsResetModalOpen,
    resetTargetUser,
    handleResetPassword,
    handleResetPasswordConfirm,
    deletingUser,
    setDeletingUser,
    isDeleting,
    handleDeleteConfirm,
    removingFromOrgUser,
    setRemovingFromOrgUser,
    isRemovingFromOrg,
    handleRemoveFromOrgConfirm,
    isAddUserModalOpen,
    setIsAddUserModalOpen,
    handleAddUserSubmit,
    revokingInvite,
    setRevokingInvite,
    handleRevokeInviteConfirm,
  } = state;

  return (
    <>
      <div className="p-6 lg:p-8">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Filters + Add Button */}
          <Card>
            <div className="p-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="sm:w-40 sm:flex-shrink-0">
                  <Select
                    value={roleFilter}
                    onChange={handleRoleChange}
                    options={ROLE_FILTER_OPTIONS}
                    placeholder="All Roles"
                  />
                </div>
                <div className="sm:w-40 sm:flex-shrink-0">
                  <Select
                    value={statusFilter}
                    onChange={handleStatusChange}
                    options={STATUS_FILTER_OPTIONS}
                    placeholder="All Statuses"
                  />
                </div>
                <div className="sm:flex-1">
                  <Input
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Search by name or email..."
                    leftIcon={<Search className="w-4 h-4" />}
                  />
                </div>
                {(roleFilter || statusFilter || searchInput) && (
                  <Button
                    variant="ghost"
                    className="text-error-600 hover:text-error-700 sm:flex-shrink-0 w-full sm:w-auto justify-center"
                    onClick={handleClearFilters}
                  >
                    <X className="w-4 h-4" />
                    Clear filters
                  </Button>
                )}
                {canWrite && (
                  <Button
                    onClick={() => setIsAddUserModalOpen(true)}
                    className="sm:flex-shrink-0 w-full sm:w-auto justify-center"
                  >
                    <Plus className="w-5 h-5" />
                    Add User
                  </Button>
                )}
              </div>
            </div>
          </Card>

          {loading ? (
            <Card variant="elevated">
              <div className="flex items-center justify-center py-16">
                <LoadingSpinner />
              </div>
            </Card>
          ) : users.length === 0 && pendingInvites.length === 0 ? (
            <Card variant="elevated">
              <EmptyState
                icon={<Users className="w-16 h-16" />}
                title="No users found"
                description="No users match your current filters."
              />
            </Card>
          ) : (
            <Card variant="elevated" padding="none">
              <Table>
                <TableHeader>
                  <TableRow className="whitespace-nowrap">
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last Login</TableHead>
                    <TableHead>Member Since</TableHead>
                    {canWrite && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">
                        {user.firstName} {user.lastName}
                        {user.id === currentUser?.id && (
                          <span className="ml-2 text-xs text-gray-400">
                            (you)
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-gray-600 dark:text-gray-400">
                        {user.email}
                      </TableCell>
                      <TableCell>
                        <Badge variant={roleVariants[user.role]} size="sm">
                          {roleLabel[user.role]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={user.isActive ? "success" : "error"}
                          size="sm"
                          dot
                        >
                          {user.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {user.lastLoginAt
                          ? formatRelativeTime(user.lastLoginAt)
                          : "—"}
                      </TableCell>
                      <TableCell className="text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {formatDate(user.createdAt)}
                      </TableCell>
                      {canWrite && (
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            {(activeRole === UserRole.Admin || activeRole === UserRole.Owner) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); navigate(`/users/${user.id}`); }}
                                title="View user details"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); handleEditUser(user); }}
                              title="Edit user"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); handleResetPassword(user); }}
                              title="Reset password"
                            >
                              <KeyRound className="w-4 h-4" />
                            </Button>
                            {activeRole === UserRole.Manager && user.id !== currentUser?.id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); setRemovingFromOrgUser(user); }}
                                className="text-error-600 hover:text-error-700"
                                title="Remove from organization"
                              >
                                <UserMinus className="w-4 h-4" />
                              </Button>
                            )}
                            {activeRole === UserRole.Admin && user.id !== currentUser?.id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); setRemovingFromOrgUser(user); }}
                                className="text-error-600 hover:text-error-700"
                                title="Remove from organization"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                            {activeRole === UserRole.Owner && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); setDeletingUser(user); }}
                                className="text-error-600 hover:text-error-700"
                                disabled={user.id === currentUser?.id}
                                title={
                                  user.id === currentUser?.id
                                    ? "You cannot delete yourself"
                                    : "Delete user"
                                }
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {pendingInvites.map((invite) => (
                    <TableRow key={invite.id} className="opacity-75">
                      <TableCell className="text-gray-400 dark:text-gray-500 italic">
                        —
                      </TableCell>
                      <TableCell className="text-gray-600 dark:text-gray-400">
                        {invite.email}
                      </TableCell>
                      <TableCell>
                        <Badge variant={roleVariants[invite.role]} size="sm">
                          {roleLabel[invite.role]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="warning" size="sm">
                          <Clock className="w-3 h-3 mr-1" />
                          Invited
                        </Badge>
                      </TableCell>
                      <TableCell className="text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        expires in {formatTimeUntil(invite.expiresAt)}
                      </TableCell>
                      <TableCell className="text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {formatDate(invite.createdAt)}
                      </TableCell>
                      {canWrite && (
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            {(activeRole === UserRole.Admin || activeRole === UserRole.Owner) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                title="Copy invite link"
                                onClick={() => navigator.clipboard.writeText(`${window.location.origin}/accept-invite?token=${invite.token}`)}
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setRevokingInvite(invite)}
                              className="text-error-600 hover:text-error-700"
                              title="Cancel invite"
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {totalPages > 1 && (
                <div className="flex items-center justify-center sm:justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                  <p className="hidden sm:block text-sm text-gray-600 dark:text-gray-400">
                    {totalCount} total users
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page <= 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page >= totalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          )}
        </div>
      </div>

      {isUserModalOpen && (
        <UserModal
          user={editingUser}
          onClose={() => setIsUserModalOpen(false)}
          onSave={handleSaveUser}
          callerRole={activeRole}
        />
      )}

      {isResetModalOpen && resetTargetUser && (
        <ResetPasswordModal
          user={resetTargetUser}
          onClose={() => setIsResetModalOpen(false)}
          onSave={handleResetPasswordConfirm}
        />
      )}

      {/* Owner-only: choose between permanent delete or remove from org */}
      <Modal
        isOpen={!!deletingUser}
        onClose={() => setDeletingUser(null)}
        title="Remove User"
        size="sm"
      >
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          What would you like to do with <strong>{deletingUser?.firstName} {deletingUser?.lastName}</strong>?
        </p>
        <div className="flex flex-col gap-3">
          <Button
            variant="outline"
            className="justify-start text-left"
            onClick={() => {
              setRemovingFromOrgUser(deletingUser);
              setDeletingUser(null);
            }}
          >
            <UserMinus className="w-4 h-4 flex-shrink-0" />
            <div>
              <p className="font-medium">Remove from Organization</p>
              <p className="text-xs text-gray-500">Removes them from this org only. Account stays intact.</p>
            </div>
          </Button>
          <Button
            variant="danger"
            className="justify-start text-left"
            isLoading={isDeleting}
            onClick={handleDeleteConfirm}
          >
            <Trash2 className="w-4 h-4 flex-shrink-0" />
            <div>
              <p className="font-medium">Permanently Delete</p>
              <p className="text-xs opacity-75">Deletes their account entirely. Cannot be undone.</p>
            </div>
          </Button>
          <Button variant="ghost" onClick={() => setDeletingUser(null)}>
            Cancel
          </Button>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!removingFromOrgUser}
        title="Remove from Organization"
        description={`Are you sure you want to remove ${removingFromOrgUser?.firstName} ${removingFromOrgUser?.lastName} from this organization? Their account will be deactivated.`}
        confirmLabel="Remove"
        onConfirm={handleRemoveFromOrgConfirm}
        onCancel={() => setRemovingFromOrgUser(null)}
        isLoading={isRemovingFromOrg}
      />

      <AddUserModal
        isOpen={isAddUserModalOpen}
        onClose={() => setIsAddUserModalOpen(false)}
        onSubmit={handleAddUserSubmit}
        callerRole={activeRole}
      />

      <ConfirmDialog
        isOpen={!!revokingInvite}
        title="Cancel Invite"
        description={`Cancel the invite sent to ${revokingInvite?.email}? They won't be able to use the link.`}
        confirmLabel="Cancel Invite"
        onConfirm={handleRevokeInviteConfirm}
        onCancel={() => setRevokingInvite(null)}
      />
    </>
  );
}
